// Fan - Balloons (aficionado)
// Reemplaza el archivo por completo con esta versión
import { useEffect, useMemo, useRef, useState } from 'react'
import { SendBalloonsTap as BLSendBalloonsTap } from './../../../utilities/BL/Zones'

/**
 * Componente fan "Globos"
 * - Habilita taps tras un countdown local (3s).
 * - Envía taps en batch (umbral o timeout).
 * - Bloquea al terminar (por winners o por tiempo local).
 * - Muestra ganadores SOLO si el winners corresponde al matchId actual.
 */
const Balloons = ({ config, winners, zone }) => {
  // ---- Estado visual
  const [uiStatus, setUiStatus] = useState('Idle')          // Idle | Countdown | Running | Finished
  const [isReady, setIsReady] = useState(false)             // habilita botón
  const [countdownMs, setCountdownMs] = useState(null)
  const [justSent, setJustSent] = useState(false)           // micro feedback de envío
  const [error, setError] = useState("")
  const [remainingMs, setRemainingMs] = useState(null)      // tiempo local restante (opcional)

  // ---- Refs (no disparan rerender)
  const queueRef = useRef(0)            // taps acumulados
  const flushingRef = useRef(false)
  const batchTimeoutRef = useRef(null)
  const countdownUntilRef = useRef(null)
  const countdownRafRef = useRef(null)
  const endLocalRef = useRef(null)
  const timeRafRef = useRef(null)

  // ---- Config estable por partida
  const [stable, setStable] = useState({
    matchId: null,
    startAt: null,
    endsAt: null,
    target: 0,
  })

  // ─────────────────────────────────────────
  // Normaliza config y no “retrocedas” campos válidos
  useEffect(() => {
    if (!config) return

    // Si es MATCH NUEVA → RESET estable
    if (config.matchId && config.matchId !== stable.matchId) {
      setStable({
        matchId: config.matchId ?? null,
        startAt: config.startAt ?? null,
        endsAt: config.endsAt ?? null,
        target: config.targetTapsPerBalloon ?? 0,
      })
      return
    }

    // Misma matchId → no retroceder
    setStable(prev => ({
      matchId: config.matchId ?? prev.matchId,
      startAt: config.startAt ?? prev.startAt,
      endsAt:   config.endsAt  ?? prev.endsAt,
      target: (config.targetTapsPerBalloon ?? prev.target) || 0,
    }))
  }, [config])
  // ─────────────────────────────────────────

  const matchId = stable.matchId

  // Derivados de tiempo de la partida (si los usas para debug)
  const durationSec = useMemo(() => {
    if (typeof config?.matchDurationSec === 'number') return Math.max(0, Math.floor(config.matchDurationSec))
    if (config?.endsAt && config?.startAt) {
      return Math.max(0, Math.floor((Date.parse(config.endsAt) - Date.parse(config.startAt)) / 1000))
    }
    return 0
  }, [config?.matchDurationSec, config?.startAt, config?.endsAt])

  // Zona normalizada
  const normalize = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
  const zoneKey = useMemo(() => normalize(zone), [zone])

  // ─────────────────────────────────────────
  // WINNERS con “scope” a la partida actual
  // (para no heredar ganadores de otra match)
  const [currentWinner, setCurrentWinner] = useState(null) // ganador vigente SOLO si coincide matchId

  useEffect(() => {
    // si cambia la partida, limpiar el ganador local
    setCurrentWinner(null)
  }, [matchId])

  useEffect(() => {
    // winners viene del padre; lo aceptamos SOLO si su matchId coincide
    if (!winners) { setCurrentWinner(null); return }

    const wMatch = winners.matchId ?? winners.MatchId ?? null
    if (!matchId || !wMatch || wMatch !== matchId) {
      // ganador de otra partida → ignóralo
      setCurrentWinner(null)
      return
    }
    setCurrentWinner(winners)
  }, [winners, matchId])
  // ─────────────────────────────────────────

  // Para logs/depuración
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[FAN] matchId:', matchId, 'target:', stable.target, 'winners(match):', winners?.matchId ?? winners?.MatchId, 'uiStatus:', uiStatus)
  }, [matchId, stable.target, winners, uiStatus])

  // ---- Envío en batch
  async function flushOnce() {
    if (!matchId || !zoneKey || flushingRef.current) return
    const toSend = Math.min(queueRef.current, 50)
    if (toSend <= 0) return

    flushingRef.current = true
    queueRef.current -= toSend

    try {
      await BLSendBalloonsTap(matchId, zoneKey, toSend)
      setJustSent(true); setTimeout(() => setJustSent(false), 80)
    } catch (e) {
      queueRef.current += toSend
      setError(e?.message || "No se pudo enviar el tap.")
    } finally {
      flushingRef.current = false
    }
  }

  const sendBatchThreshold = config?.sendBatchThreshold ?? 10    // umbral
  const sendBatchTimeoutMs = config?.sendBatchTimeoutMs ?? null  // timeout opcional

  // ---- Reset duro al cambiar de partida (limpia timers/colas/UI)
  useEffect(() => {
    // cancelar timers previos
    if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null }
    if (timeRafRef.current)      { cancelAnimationFrame(timeRafRef.current); timeRafRef.current = null }
    if (countdownRafRef.current) { cancelAnimationFrame(countdownRafRef.current); countdownRafRef.current = null }

    // limpiar colas/flags
    queueRef.current = 0
    flushingRef.current = false

    // limpiar UI/estados locales
    setIsReady(false)
    setJustSent(false)
    setError('')
    setRemainingMs(null)
    setCountdownMs(null)
    setUiStatus('Idle')
    setCurrentWinner(null) // ← importante: no heredar ganador anterior
  }, [matchId])

  // ---- Countdown local fijo (3s) al entrar a nueva partida
  useEffect(() => {
    if (!matchId) {
      setUiStatus('Idle'); setIsReady(false); setCountdownMs(null)
      return
    }

    // 3s
    const until = Date.now() + 3000
    countdownUntilRef.current = until
    setCountdownMs(3000)
    setUiStatus('Countdown')
    setIsReady(false)

    const tick = () => {
      const left = Math.max(0, countdownUntilRef.current - Date.now())
      setCountdownMs(left)
      if (left <= 0) {
        setUiStatus('Running')
        setIsReady(true)
        cancelAnimationFrame(countdownRafRef.current)
        return
      }
      countdownRafRef.current = requestAnimationFrame(tick)
    }

    cancelAnimationFrame(countdownRafRef.current)
    countdownRafRef.current = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(countdownRafRef.current)
  }, [matchId])

  // ---- Reloj local de la ronda (para bloquear al terminar aunque winners llegue tarde)
  useEffect(() => {
    if (uiStatus !== 'Running') return
    if (!durationSec) return // si no hay duración, no corras reloj

    const durMs = Math.max(0, durationSec * 1000)
    endLocalRef.current = Date.now() + durMs
    setRemainingMs(durMs)

    const tick = () => {
      const left = Math.max(0, endLocalRef.current - Date.now())
      setRemainingMs(left)
      if (left <= 0) {
        setUiStatus('Finished')
        setIsReady(false)
        // flush final por si quedó cola
        flushOnce()
        return
      }
      timeRafRef.current = requestAnimationFrame(tick)
    }

    cancelAnimationFrame(timeRafRef.current)
    timeRafRef.current = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(timeRafRef.current)
  }, [uiStatus, durationSec])

  // ---- Si llega winners (de la partida actual), bloquea y muestra
  useEffect(() => {
    if (!currentWinner) return
    setUiStatus('Finished')
    setIsReady(false)

    if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null }
    cancelAnimationFrame(timeRafRef.current)

    flushOnce() // envía lo que quede
  }, [currentWinner])

  // ---- Handler Tap (acumula y decide flush)
  const alreadyFinished = (uiStatus === 'Finished') || !!currentWinner
  const handleTap = () => {
    if (!isReady || alreadyFinished || !matchId || !zoneKey) return

    // 1) Acumula con techo (evitar colas gigantes)
    if (queueRef.current < 200) queueRef.current += 1

    // 2) Umbral → flush inmediato
    if (queueRef.current >= (sendBatchThreshold || 10)) {
      if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null }
      flushOnce()
      return
    }

    // 3) Programar timeout si aplica
    if (sendBatchTimeoutMs && sendBatchTimeoutMs > 0) {
      if (batchTimeoutRef.current) clearTimeout(batchTimeoutRef.current)
      batchTimeoutRef.current = setTimeout(() => {
        batchTimeoutRef.current = null
        flushOnce()
      }, sendBatchTimeoutMs)
    }
  }

  // ---- Derivados visuales
  const showCountdown = uiStatus === 'Countdown' && (countdownMs ?? 0) > 0
  const countdownSeconds = showCountdown ? Math.ceil(countdownMs / 1000) : 0

  const showTimeLeft = uiStatus === 'Running' && (remainingMs ?? 0) > 0
  const timeLeftSec = showTimeLeft ? Math.ceil(remainingMs / 1000) : 0

  const buttonDisabled = !isReady || alreadyFinished || !matchId
  const showWinners = !!currentWinner  // SOLO si ganador corresponde a esta partida

  return (
    <div className="Balloons__Fan">
      {showCountdown && (
        <>
          <div className='Balloons__Fan__Countdown--Background'></div>
          <div className="Balloons__Fan__Countdown">
            <p>{countdownSeconds > 0 ? countdownSeconds : "¡YA!"}</p>
          </div>
        </>
      )}

      {showWinners && (
        <div className="Balloons__Fan__Winners">
          {currentWinner.winnerType === "single" && (
            currentWinner.winners?.[0] === zone ? (
              <div className='Balloons__Fan__Winners'>
                <p className='Balloons__Fan__Winners__Icon'><i className="bi bi-trophy"></i></p>
                <p className='Balloons__Fan__Winners__Text'>¡Felicidades!</p>
                <p className='Balloons__Fan__Winners__Text'>Ha ganado tu zona</p>
              </div>
            ) : (
              <div className='Balloons__Fan__Winners'>
                <p className='Balloons__Fan__Winners__Icon'><i className="bi bi-trophy"></i></p>
                <p className='Balloons__Fan__Winners__Text'>La zona ganadora es...</p>
                <p className='Balloons__Fan__Winners__Text'>Zona {currentWinner.winners?.[0]}</p>
              </div>
            )
          )}

          {currentWinner.winnerType === "tie" && (
            <div className='Balloons__Fan__Winners'>
              <p className='Balloons__Fan__Winners__Text'>Empate entre {(currentWinner.winners || []).join(", ")}</p>
            </div>
          )}

          {currentWinner.winnerType === "all-tie" && (
            <div className='Balloons__Fan__Winners'>
              <p className='Balloons__Fan__Winners__Text'>¡Han empatado todas las zonas!</p>
            </div>
          )}
        </div>
      )}

      {showTimeLeft && (
        <p className="Balloons__Fan__TimeLeft">Tiempo restante: {timeLeftSec} seg.</p>
      )}

      <button
        type="button"
        className={`Balloons__Fan__Winners__Button ${buttonDisabled ? "Balloons__Fan__Winners__Button--IsDisabled" : ""} ${justSent ? "Balloons__Fan__Winners__Button--IsPulse" : ""}`}
        onClick={handleTap}
        disabled={buttonDisabled}
      >
        <span className="Balloons__Fan__Button__Text">
          {alreadyFinished
            ? "Finalizado"
            : !matchId
              ? "Esperando..."
              : isReady
                ? "¡Tap! ¡Tap!"
                : showCountdown
                  ? '¡Prepárate!'
                  : "Esperando..."}
        </span>
      </button>

      {error && <p className="Balloons__Fan__Winners__Text--Danger">{error}</p>}
    </div>
  )
}

export { Balloons }















// import { useEffect, useMemo, useRef, useState } from 'react'

// import { SendBalloonsTap as BLSendBalloonsTap } from './../../../utilities/BL/Zones'


// /**
//  * Componente fan "Globos"
//  * - Habilita taps a partir de startAt (countdown local).
//  * - Envía taps en batches cada ~120ms (máx 50 por envío).
//  * - Se desactiva al terminar (winners || endsAt).
//  */
// const Balloons = ({ config, winners, zone }) => {
//   // ---- Estado visual
//   const [isReady, setIsReady] = useState(false)       // habilita botón
//   const [countdownMs, setCountdownMs] = useState(null)
//   const [justSent, setJustSent] = useState(false)     // micro feedback de envío
//   const [error, setError] = useState("")

//   // ---- Refs (no disparan rerender)
//   const queueRef = useRef(0)                          // taps acumulados
//   const flushingRef = useRef(false)
//   const flushTimerRef = useRef(null)
//   const batchTimeoutRef = useRef(null);  // one-shot timer para timeout opcional
  
//   const [remainingMs, setRemainingMs] = useState(null);
//   const endLocalRef = useRef(null);
//   const timeRafRef = useRef(null);

//   // --- UI local (no dependemos del reloj del dispositivo)
//   const [uiStatus, setUiStatus] = useState('Idle')   // Idle | Countdown | Running | Finished
//   const countdownUntilRef = useRef(null)
//   const countdownRafRef = useRef(null)
  
//   // ---- Config "estable" (no regresa a espera si ya hay tiempos válidos)
//   const [stable, setStable] = useState({
//     matchId: null,
//     startAt: null,
//     endsAt: null,
//     target: 0,
//   })

//   async function flushOnce() {
//     if (!matchId || !zoneKey || flushingRef.current) return;
//     const toSend = Math.min(queueRef.current, 50);
//     if (toSend <= 0) return;

//     flushingRef.current = true;
//     queueRef.current -= toSend;

//     try {
//       await BLSendBalloonsTap(matchId, zoneKey, toSend);
//       setJustSent(true); setTimeout(() => setJustSent(false), 80);
//     } catch (e) {
//       queueRef.current += toSend;
//       setError(e?.message || "No se pudo enviar el tap.");
//     } finally {
//       flushingRef.current = false;
//     }
//   }

//   // Normaliza y “no regreses” si llega un snapshot viejo/incompleto
//   useEffect(() => {
//     if (!config) return

//     // Si es match nuevo → resetea por completo
//     if (config.matchId && config.matchId !== stable.matchId) {
//       setStable({
//         matchId: config.matchId ?? null,
//         startAt: config.startAt ?? null,
//         endsAt: config.endsAt ?? null,
//         target: config.targetTapsPerBalloon ?? 0,
//       })
//       return
//     }

//     // Misma matchId → no retroceder campos ya válidos
//     setStable(prev => ({
//       matchId: config.matchId ?? prev.matchId,
//       startAt: config.startAt ?? prev.startAt,      // si viene vacío, conserva el anterior
//       endsAt: config.endsAt ?? prev.endsAt,
//       target: (config.targetTapsPerBalloon ?? prev.target) || 0,
//     }))
//   }, [config])

//   const matchId = stable.matchId

//   const startAtDate = useMemo(
//     () => (stable.startAt ? new Date(stable.startAt) : null),
//     [stable.startAt]
//   )

//   const endsAtDate = useMemo(
//     () => (stable.endsAt ? new Date(stable.endsAt) : null),
//     [stable.endsAt]
//   )

//   // const durationSec = config?.matchDurationSec
//   // ?? ((config?.endsAt && config?.startAt)
//   //     ? Math.max(0, Math.floor((Date.parse(config.endsAt) - Date.parse(config.startAt)) / 1000))
//   //     : 0)   
//   const durationSec = useMemo(() => {
//     // 1) Preferimos el valor directo del server si viene
//     if (typeof config?.matchDurationSec === 'number') {
//       return Math.max(0, Math.floor(config.matchDurationSec));
//     }
//     // 2) Si no viene, lo calculamos de start/end
//     if (config?.endsAt && config?.startAt) {
//       return Math.max(
//         0,
//         Math.floor((Date.parse(config.endsAt) - Date.parse(config.startAt)) / 1000)
//       );
//     }
//     return 0;
//   }, [config?.matchDurationSec, config?.startAt, config?.endsAt]);

//   // Si implementaste el temporizador local: úsalo.
//   // Con esto, aunque no llegue balloon.show-winners por red, al llegar el fin local se bloquea el botón y la UX no queda “colgada”.
//   const alreadyFinished = (uiStatus === 'Finished') || !!winners

//   const normalize = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
//   const zoneKey = useMemo(() => normalize(zone), [zone])

//   /**
//    * Reset duro cuando cambia la match (colas/timers/flags/UI)
//    * Garantiza que no arrastras timers ni colas de la partida anterior (evitas comportamientos raros y “pantallazos”)
//    */
//   useEffect(() => {
//     // cancela timers de batch y animaciones previas
//     if (batchTimeoutRef.current) {
//       clearTimeout(batchTimeoutRef.current)
//       batchTimeoutRef.current = null
//     }

//     if (timeRafRef.current) {
//       cancelAnimationFrame(timeRafRef.current)
//       timeRafRef.current = null
//     }

//     if (countdownRafRef.current) {
//       cancelAnimationFrame(countdownRafRef.current)
//       countdownRafRef.current = null
//     }

//     // limpia colas/flags
//     queueRef.current = 0
//     flushingRef.current = false

//     // limpia UI/estados locales
//     setIsReady(false)
//     setJustSent(false)
//     setError('')
//     setRemainingMs?.(null)   // solo si usas el temporizador local de tiempo restante
//     setCountdownMs(null)
//     setUiStatus('Idle')      // para que el siguiente efecto pase a 'Countdown'
//   }, [matchId])

//   /**
//    * Countdown fijo de 3s cuando llega una match nueva
//    * Se enfoca solo en iniciar el countdown de la nueva partida
//    */
//   useEffect(() => {
//     // si no hay match no hay nada que hacer
//     if (!matchId) {
//       setUiStatus('Idle');
//       setIsReady(false);
//       setCountdownMs(null);
//       return;
//     }

//     // Arranca countdown solo si veníamos de Idle o si cambió la match
//     // (stable.matchId ya lo estás manteniendo; aquí usamos matchId de stable)
//     setUiStatus(prev => (prev === 'Idle' || prev === 'Finished') ? 'Countdown' : prev);

//     // si ya estábamos en 'Countdown' o 'Running', no reiniciar
//     if (uiStatus === 'Countdown' || uiStatus === 'Running') return;

//     // programa 3s
//     const until = Date.now() + 3000;
//     countdownUntilRef.current = until;
//     setCountdownMs(3000);
//     setIsReady(false);

//     const tick = () => {
//       const left = Math.max(0, countdownUntilRef.current - Date.now());
//       setCountdownMs(left);
//       if (left <= 0) {
//         setUiStatus('Running');
//         setIsReady(true);
//         countdownRafRef.current && cancelAnimationFrame(countdownRafRef.current);
//         return;
//       }
//       countdownRafRef.current = requestAnimationFrame(tick);
//     };

//     countdownRafRef.current && cancelAnimationFrame(countdownRafRef.current);
//     countdownRafRef.current = requestAnimationFrame(tick);

//     return () => {
//       countdownRafRef.current && cancelAnimationFrame(countdownRafRef.current);
//     };
//   }, [matchId]); // <- solo depende de la match; NO de startAt/endsAt

//   // Cuando termina el 3-2-1 local, arrancas Running (ya lo haces).
//   // Añade este efecto para iniciar el contador local:
//   useEffect(() => {
//     if (uiStatus !== 'Running') return

//     // Si ya tenemos winners, no arranques el reloj
//     if (winners) return

//     // Fija el fin local: ahora + duración
//     const durMs = Math.max(0, (durationSec || 0) * 1000)
//     endLocalRef.current = Date.now() + durMs
//     setRemainingMs(durMs)

//     const tick = () => {
//       const left = Math.max(0, endLocalRef.current - Date.now())
//       setRemainingMs(left)
//       if (left <= 0) {
//         setUiStatus('Finished')
//         setIsReady(false)
//         // Flush final por si quedó cola
//         if (typeof flushOnce === 'function') flushOnce()
//         return
//       }
//       timeRafRef.current = requestAnimationFrame(tick)
//     }

//     timeRafRef.current && cancelAnimationFrame(timeRafRef.current)
//     timeRafRef.current = requestAnimationFrame(tick)

//     return () => {
//       timeRafRef.current && cancelAnimationFrame(timeRafRef.current)
//     }
//   }, [uiStatus, durationSec, winners])

//   const sendBatchThreshold = config?.sendBatchThreshold ?? 10;   // umbral principal
//   const sendBatchTimeoutMs = config?.sendBatchTimeoutMs ?? null; // ms (null = off)

//   // ---- Click handler (solo acumula taps en memoria)
//   const handleTap = () => {
//     if (!isReady || alreadyFinished || !matchId || !zoneKey) return
    
//     // 1) Acumula. Evita que crezca infinito: deja cola máxima de 200 para no saturar
//     if (queueRef.current < 200) queueRef.current += 1

//     // 2) Si alcanzamos el umbral → flush inmediato
//     if (queueRef.current >= (sendBatchThreshold || 10)) {
//       // cancela un timeout pendiente (si lo hubiera)
//       if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null; }
//       flushOnce()
//       return
//     }

//     // 3) si no alcanzamos umbral y hay timeout configurado → programa/renueva one‑shot
//     if (sendBatchTimeoutMs && sendBatchTimeoutMs > 0) {
//       if (batchTimeoutRef.current) clearTimeout(batchTimeoutRef.current)
//         batchTimeoutRef.current = setTimeout(() => {
//         batchTimeoutRef.current = null
//         flushOnce()
//       }, sendBatchTimeoutMs)
//     }
//   }

//   // ---- Derivados visuales
//   const showCountdown = uiStatus === 'Countdown' && (countdownMs ?? 0) > 0
//   const countdownSeconds = showCountdown ? Math.ceil(countdownMs / 1000) : 0

//   const showTimeLeft = uiStatus === 'Running' && (remainingMs ?? 0) > 0
//   const timeLeftSec = showTimeLeft ? Math.ceil(remainingMs / 1000) : 0

//   const buttonDisabled = !isReady || alreadyFinished || !matchId
//   const showWinners = !!winners
  
//   // Al llegar winners (fin), bloquea UI
//   useEffect(() => {
//     if (!winners) return

//     setUiStatus('Finished')
//     setIsReady(false)

//     if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null; }

//     timeRafRef.current && cancelAnimationFrame(timeRafRef.current)

//     if (typeof flushOnce === 'function') flushOnce() // envía lo que quede
//   }, [winners])

//   // Al cambiar matchId (nueva partida)
//   useEffect(() => {
//     // limpiar timers y cola de la partida anterior
//     if (batchTimeoutRef.current) { clearTimeout(batchTimeoutRef.current); batchTimeoutRef.current = null; }
//     queueRef.current = 0
//     flushingRef.current = false
//   }, [matchId])

//   // Al ocultar/cerrar pestaña: intenta flush final
//   useEffect(() => {
//     const onHide = () => { flushOnce() }
//     const onUnload = () => { /* opcional: navigator.sendBeacon si tienes endpoint HTTP */ }

//     document.addEventListener('visibilitychange', onHide)
//     window.addEventListener('pagehide', onHide)
//     window.addEventListener('beforeunload', onUnload)

//     return () => {
//       document.removeEventListener('visibilitychange', onHide)
//       window.removeEventListener('pagehide', onHide)
//       window.removeEventListener('beforeunload', onUnload)
//     };
//   }, [matchId, zoneKey])

//   //useEffect(() => { if (config) console.log('FAN PREPARE:', config) }, [config])


//   return (
//     <div className="Balloons__Fan">
//       {showCountdown && (
//         <>
//           <div className='Balloons__Fan__Countdown--Background'></div>
//           <div className="Balloons__Fan__Countdown">
//             <p>{countdownSeconds > 0 ? countdownSeconds : "¡YA!"}</p>
//           </div>
//         </>
//       )}

//       {showWinners && (
//         <div className="Balloons__Fan__Winners">
//           {winners.winnerType === "single" && (
//             winners.winners?.[0] === zone ? (
//               <div className='Balloons__Fan__Winners'>
//                 <p className='Balloons__Fan__Winners__Icon'><i className="bi bi-trophy"></i></p>
//                 <p className='Balloons__Fan__Winners__Text'>¡Felicidades!</p>
//                 <p className='Balloons__Fan__Winners__Text'>Ha ganado tu zona</p>
//               </div>
//             ) : (
//               <div className='Balloons__Fan__Winners'>
//                 <p className='Balloons__Fan__Winners__Icon'><i className="bi bi-trophy"></i></p>
//                 <p className='Balloons__Fan__Winners__Text'>La zona ganadora es...</p>
//                 <p className='Balloons__Fan__Winners__Text'>Zona {winners.winners?.[0]}</p>
//               </div>
//             )
//           )}

//           {winners.winnerType === "tie" && (
//             <div className='Balloons__Fan__Winners'>
//               <p className='Balloons__Fan__Winners__Text'>Ha habido un empate entre las zonas {(winners.winners || []).join(", ")}</p>
//             </div>
//           )}

//           {winners.winnerType === "all-tie" && (
//             <div className='Balloons__Fan__Winners'>
//               <p className='Balloons__Fan__Winners__Text'>¡Han empatado todas las zonas!</p>
//             </div>
//           )}
//         </div>
//       )}

//       {showTimeLeft && (
//         <p className="Balloons__Fan__TimeLeft">Tiempo restante: {timeLeftSec} seg.</p>
//       )}

//       <button type="button" className={`Balloons__Fan__Winners__Button ${buttonDisabled ? "Balloons__Fan__Winners__Button--IsDisabled" : ""} ${justSent ? "Balloons__Fan__Winners__Button--IsPulse" : ""}`} onClick={handleTap} disabled={buttonDisabled}>
//         <span className="Balloons__Fan__Button__Text">
//           {alreadyFinished
//             ? "Finalizado"
//             : !matchId
//               ? "Esperando..."
//               : isReady
//                 ? "¡Tap! ¡Tap!"
//                 : showCountdown
//                   ? '¡Prepárate!'
//                   : "Esperando..."}
//         </span>
//       </button>

//       {error && <p className="Balloons__Fan__Winners__Text--Danger">{error}</p>}
//     </div>
//   )
// }

// export { Balloons };